import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCurrency } from "@/lib/format";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type SupplierCreditNoteResponse = components["schemas"]["SupplierCreditNoteResponse"];
type BillResponse = components["schemas"]["BillResponse"];

// States in which a bill can have supplier credit netted against it (must mirror the backend).
const CREDITABLE_BILL_STATES = new Set(["NeedsApproval", "Approved", "Scheduled", "PaymentFailed", "PartiallyPaid"]);

export interface ApplySupplierCreditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creditNote: SupplierCreditNoteResponse | null;
  onApplied: () => void;
}

export function ApplySupplierCreditDialog({
  open,
  onOpenChange,
  creditNote,
  onApplied,
}: ApplySupplierCreditDialogProps) {
  const api = useDolfinClient();
  const [bills, setBills] = useState<BillResponse[]>([]);
  const [billId, setBillId] = useState("");
  const [amount, setAmount] = useState("");
  const [loadingBills, setLoadingBills] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // When the bill isn't in the recent-50 list, the user can paste a bill ID directly.
  const [manualEntry, setManualEntry] = useState(false);

  const eligibleBills = useMemo(() => {
    if (!creditNote) return [];
    return bills.filter(
      (b) =>
        b.supplierId === creditNote.supplierId &&
        b.currency === creditNote.currency &&
        b.state != null &&
        CREDITABLE_BILL_STATES.has(b.state) &&
        Number(b.outstanding ?? 0) > 0,
    );
  }, [bills, creditNote]);

  // Only the 50 most recent bills; older ones go through manual bill-ID entry.
  useEffect(() => {
    if (!open || !creditNote) return;
    let cancelled = false;
    (async () => {
      setLoadingBills(true);
      try {
        const { data } = await api.GET("/v1/bills", { params: { query: { Limit: 50 } } });
        if (!cancelled) setBills(data?.data ?? []);
      } finally {
        if (!cancelled) setLoadingBills(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, open, creditNote]);

  const selectedBill = eligibleBills.find((b) => b.id === billId);
  // Whichever runs out first, credit or what's owed; for a pasted bill ID only the credit caps it.
  const maxApplicable = creditNote
    ? selectedBill
      ? Math.min(Number(creditNote.available ?? 0), Number(selectedBill.outstanding ?? 0))
      : Number(creditNote.available ?? 0)
    : 0;

  useEffect(() => {
    if (!billId || !creditNote) return;
    const bill = eligibleBills.find((b) => b.id === billId);
    if (!bill) return;
    const max = Math.min(Number(creditNote.available ?? 0), Number(bill.outstanding ?? 0));
    setAmount(max.toFixed(2));
  }, [billId, creditNote, eligibleBills]);

  function resetForm() {
    setBillId("");
    setAmount("");
    setError(null);
    setSubmitting(false);
    setManualEntry(false);
  }

  function handleOpenChange(next: boolean) {
    if (!next) resetForm();
    onOpenChange(next);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!creditNote) return;
    setError(null);

    if (!billId) {
      setError("Select a bill to apply the credit to.");
      return;
    }
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError("Enter an amount greater than zero.");
      return;
    }
    if (amt > maxApplicable + 0.005) {
      setError(`That is more than can be applied. The most is ${formatCurrency(maxApplicable, creditNote.currency)}.`);
      return;
    }

    setSubmitting(true);
    const { error: apiError } = await api.POST("/v1/supplier-credit-notes/{creditNoteId}/applications", {
      params: { path: { creditNoteId: creditNote.id! } },
      body: { billId, amount: amt },
    });

    if (apiError) {
      const message = apiErrorMessage(apiError, "Failed to apply credit.");
      setError(message);
      toast.error(message);
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    handleOpenChange(false);
    toast.success("Credit applied to bill");
    onApplied();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Apply supplier credit</DialogTitle>
            <DialogDescription>
              {creditNote
                ? `Net ${formatCurrency(creditNote.available, creditNote.currency)} available from ${creditNote.documentNumber}. Choose a bill you owe this supplier.`
                : ""}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="apply-bill">Bill</Label>
                <button
                  type="button"
                    className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                  onClick={() => {
                    setManualEntry((m) => !m);
                    setBillId("");
                    setAmount("");
                  }}
                >
                  {manualEntry ? "Choose from list" : "Enter bill ID"}
                </button>
              </div>
              {manualEntry ? (
                <Input
                  id="apply-bill"
                  placeholder="Paste a bill ID"
                  value={billId}
                  onChange={(e) => setBillId(e.target.value.trim())}
                />
              ) : (
                <Select value={billId} onValueChange={setBillId} disabled={loadingBills}>
                  <SelectTrigger id="apply-bill">
                    <SelectValue placeholder={loadingBills ? "Loading bills…" : "Select a bill"} />
                  </SelectTrigger>
                  <SelectContent>
                    {eligibleBills.map((b) => (
                      <SelectItem key={b.id} value={b.id!}>
                        {(b.invoiceNumber || b.supplierName || b.id)} ·{" "}
                        {formatCurrency(b.outstanding, b.currency ?? "GBP")} outstanding
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {!manualEntry && !loadingBills && eligibleBills.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No recent bills from this supplier can take this credit. It needs an approved or
                  scheduled bill in {creditNote?.currency} with a balance left to pay. Use Enter bill ID
                  to apply it to an older bill.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="apply-amount">Amount</Label>
                {creditNote && maxApplicable > 0 ? (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground tabular-nums hover:text-foreground hover:underline"
                    onClick={() => setAmount(maxApplicable.toFixed(2))}
                  >
                    Up to {formatCurrency(maxApplicable, creditNote.currency)}
                  </button>
                ) : null}
              </div>
              <Input
                id="apply-amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>

            {selectedBill ? (
              <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 rounded-lg border bg-muted/30 px-3 py-2.5 text-sm">
                <dt className="text-muted-foreground">Owed on this bill</dt>
                <dd className="text-right tabular-nums">
                  {formatCurrency(selectedBill.outstanding, selectedBill.currency ?? creditNote?.currency)}
                </dd>
                <dt className="text-muted-foreground">Left to pay after credit</dt>
                <dd className="text-right font-medium tabular-nums">
                  {formatCurrency(
                    Math.max(0, Number(selectedBill.outstanding ?? 0) - (parseFloat(amount) || 0)),
                    selectedBill.currency ?? creditNote?.currency,
                  )}
                </dd>
              </dl>
            ) : null}

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={submitting || !billId}>
              {submitting && <Spinner className="size-4" />}
              Apply credit
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
